import { memo, useCallback, useMemo } from "react";
import { clsx } from "clsx";
import { IGif } from "services/types";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { DraggableTile } from "../DraggableTile";
import { adaptTileProps, reorder } from "./utils";
import PinnedIcon from "components/icons/pinned.svg?react";
import { TOnPinParams } from "../usePinsGif";
import { metrics } from "utils/metrics";
import "./PinnedGifs.scss";

interface IPinnedGifsProps {
  gifs: IGif[];
  showPlaceholders: boolean;
  onChangePins: (pins: IGif[]) => void;
  onPinGif: (params: TOnPinParams) => void;
}

export const PinnedGifs: React.FC<IPinnedGifsProps> = memo(({
  gifs,
  showPlaceholders,
  onChangePins,
  onPinGif
}) => {
  const placeholders = useMemo(() =>
    showPlaceholders ? new Array(4 - gifs.length).fill(null) : [],
  [gifs, showPlaceholders]);

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) {
      return;
    }

    const newItems = reorder(gifs, source.index, destination.index);
    onChangePins(newItems);
  };

  const handleClick = useCallback(() => {
    metrics.sendPinnedContentClick();
  }, []);

  return (
    <div className={clsx("PinnedGifs", !placeholders.length && !gifs.length && "PinnedGifs_isEmpty")}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-file-list" direction="horizontal">
          {provided => (
            <div
              ref={provided.innerRef}
              className="PinnedGifs-Wrap"
              {...provided.droppableProps}
            >
              {gifs.map((gif: IGif, index) => (
                <DraggableTile
                  isDragDisabled={!showPlaceholders}
                  key={gif.id}
                  gifId={gif.id}
                  tileItemProps={{
                    ...adaptTileProps({ gif, onPinGif, showPlaceholders }),
                    index,
                    onClick: handleClick
                  }}
                />
              ))}
              {provided.placeholder}
              {placeholders.map((_, index) => (
                <div key={index} className={clsx("ItemPlaceholder", index === 0 && "ItemPlaceholder_isFirst")}>
                  <PinnedIcon className="PinnedIcon" />
                </div>
              ))}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
});
